import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  Eye, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Package, 
  Star,
  Settings,
  Gauge,
  Monitor,
  Droplets,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Menu,
  X,
  ArrowUp,
  Instagram
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

console.log('🔍 Frontend Config:', { BACKEND_URL, API });

export const Home = () => {
  const [formData, setFormData] = useState({
    firma: '',
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    maschinentyp: '',
    breite: '',
    hoehe: '',
    nachricht: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [formSubmittedSuccessfully, setFormSubmittedSuccessfully] = useState(false);

  // Scroll-to-Top Button Visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Check initial position
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send inquiry to backend (saves to DB + sends email via Strato SMTP)
      const response = await axios.post(`${API}/inquiry`, formData);
      
      if (response.data.success) {
        toast.success('Anfrage gesendet – wir melden uns in Kürze!', {
          description: 'Ihre Anfrage wurde erfolgreich übermittelt.',
          duration: 5000,
        });
        
        // Show download section
        setFormSubmittedSuccessfully(true);
        
        // Reset form
        setFormData({
          firma: '',
          vorname: '',
          nachname: '',
          email: '',
          telefon: '',
          maschinentyp: '',
          breite: '',
          hoehe: '',
          nachricht: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Fehler beim Senden der Anfrage', {
        description: 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per E-Mail.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    // Close mobile menu when clicking a link
    setIsMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold text-gray-900">
            SafeTec<span className="text-blue-600">+</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('solutions')} className="text-gray-600 hover:text-gray-900 transition-colors">
              Lösungen
            </button>
            <button onClick={() => scrollToSection('applications')} className="text-gray-600 hover:text-gray-900 transition-colors">
              Einsatzbereiche
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-gray-900 transition-colors">
              So funktioniert's
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-gray-900 transition-colors">
              FAQ
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-gray-900 transition-colors">
              Kontakt
            </button>
          </nav>
          
          {/* Desktop CTA Button & Instagram */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Instagram Button with colorful gradient logo */}
            <a
              href="https://www.instagram.com/safetec_plus_industrieschutz"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Instagram"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <radialGradient id="instagramGradient" cx="30%" cy="107%" r="150%">
                    <stop offset="0%" style={{ stopColor: '#fdf497', stopOpacity: 1 }} />
                    <stop offset="5%" style={{ stopColor: '#fdf497', stopOpacity: 1 }} />
                    <stop offset="45%" style={{ stopColor: '#fd5949', stopOpacity: 1 }} />
                    <stop offset="60%" style={{ stopColor: '#d6249f', stopOpacity: 1 }} />
                    <stop offset="90%" style={{ stopColor: '#285AEB', stopOpacity: 1 }} />
                  </radialGradient>
                </defs>
                <path
                  d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                  fill="url(#instagramGradient)"
                />
              </svg>
            </a>
            
            <Button onClick={() => scrollToSection('contact')} className="bg-blue-600 hover:bg-blue-700">
              Anfrage starten
            </Button>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('solutions')} 
                className="text-left text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
              >
                Lösungen
              </button>
              <button 
                onClick={() => scrollToSection('applications')} 
                className="text-left text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
              >
                Einsatzbereiche
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="text-left text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
              >
                So funktioniert's
              </button>
              <button 
                onClick={() => scrollToSection('faq')} 
                className="text-left text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
              >
                FAQ
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="text-left text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
              >
                Kontakt
              </button>
              
              {/* Instagram Link for Mobile */}
              <a
                href="https://www.instagram.com/safetec_plus_industrieschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors py-2 border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <radialGradient id="instagramGradientMobile" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" style={{ stopColor: '#fdf497', stopOpacity: 1 }} />
                      <stop offset="5%" style={{ stopColor: '#fdf497', stopOpacity: 1 }} />
                      <stop offset="45%" style={{ stopColor: '#fd5949', stopOpacity: 1 }} />
                      <stop offset="60%" style={{ stopColor: '#d6249f', stopOpacity: 1 }} />
                      <stop offset="90%" style={{ stopColor: '#285AEB', stopOpacity: 1 }} />
                    </radialGradient>
                  </defs>
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                    fill="url(#instagramGradientMobile)"
                  />
                </svg>
                <span>Instagram</span>
              </a>
              
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="bg-blue-600 hover:bg-blue-700 w-full mt-2"
              >
                Anfrage starten
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="container mx-auto px-4 max-w-full overflow-hidden">
          <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-16 items-center">
            <div className="space-y-6 w-full overflow-hidden">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium border border-blue-100">
                <Shield className="h-4 w-4" />
                <span>Maschinenschutz für CNC-Betriebe</span>
              </div>
              
              <div className="space-y-4 w-full overflow-hidden">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] break-words">
                  Schützt Ihre Sichtscheiben, bevor aus kleinen Schäden teure Stillstände werden
                </h1>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed break-words">
                  Maßgeschnittene Schutzlösungen für Maschinenverglasung, Hotspot-Bereiche und Displays. Entwickelt für CNC-Bearbeitung, Drehen und Fräsen.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button 
                  onClick={() => scrollToSection('contact')} 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base font-medium w-full sm:w-auto"
                >
                  <span className="block sm:inline">Maße senden & Empfehlung erhalten</span>
                  <ArrowRight className="ml-2 h-5 w-5 hidden sm:inline" />
                </Button>
                <Button 
                  onClick={() => scrollToSection('solutions')} 
                  size="lg" 
                  variant="outline"
                  className="text-sm sm:text-base border-gray-300 font-medium w-full sm:w-auto"
                >
                  Produktübersicht
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700 font-medium">Nach Maß zugeschnitten</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700 font-medium">Montagefertig</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700 font-medium">Hamburg – HS Hanse Trade KG</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700 font-medium">Versand in der Regel innerhalb von ca. 5 Werktagen</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-2xl ring-1 ring-gray-900/5">
                <picture>
                  <source srcSet="/images/cnc-maschine-sichtfenster.webp" type="image/webp" />
                  <img 
                    src="/images/cnc-maschine-sichtfenster.jpg" 
                    alt="CNC-Maschine mit Sichtfenster"
                    className="w-full h-full object-cover"
                    width="1920"
                    height="1280"
                    loading="eager"
                    fetchpriority="high"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-lg shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Ab</div>
                    <div className="text-xl font-bold text-gray-900">231,50 €</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4 max-w-full overflow-hidden">
          <div className="text-center max-w-3xl mx-auto mb-12 w-full overflow-hidden px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 break-words">
              Kunden kaufen keine Folie. Sie kaufen Sichtbarkeit, Schutz und weniger Stillstand.
            </h2>
            <p className="text-base text-gray-600 break-words">
              Die Kosten entstehen durch Produktionsausfälle und ungeplante Maschinenstillstände – nicht durch die Scheibe selbst.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all group">
              <div className="w-full h-40 overflow-hidden">
                <picture>
                  <source srcSet="/images/funkenflug-metallbearbeitung.webp" type="image/webp" />
                  <img 
                    src="/images/funkenflug-metallbearbeitung.jpg" 
                    alt="Funkenflug bei Metallbearbeitung"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1200"
                    height="800"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <Eye className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-bold text-gray-900">Schlechte Sicht = längere Zykluszeiten</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Verkratzte oder milchige Scheiben führen zu häufigerem Türöffnen, längeren Bearbeitungszeiten und erhöhtem Ausschussrisiko.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all group">
              <div className="w-full h-40 overflow-hidden">
                <picture>
                  <source srcSet="/images/automatisierte-fertigung.webp" type="image/webp" />
                  <img 
                    src="/images/automatisierte-fertigung.jpg" 
                    alt="Industrielle Bearbeitung"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1200"
                    height="800"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-bold text-gray-900">Scheibenaustausch: 500–2000 € + Stillstand</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Original-Maschinenscheiben sind teuer. Plus Montageaufwand und Produktionsausfall – vermeidbare Kosten.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all group">
              <div className="w-full h-40 overflow-hidden">
                <picture>
                  <source srcSet="/images/moderne-produktion.webp" type="image/webp" />
                  <img 
                    src="/images/moderne-produktion.jpg" 
                    alt="Hotspot-Bereich bei Bearbeitung"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="800"
                    height="1200"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <Clock className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-bold text-gray-900">Hotspot-Bereiche bei Drehmaschinen</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Bereich gegenüber Spindel/Futter: Maximale Beanspruchung durch Späne und Kühlmittel. Hier versagt die Scheibe zuerst.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solutions" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Produktübersicht</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Drei Kernlösungen – aufeinander abgestimmt
            </h2>
            <p className="text-base text-gray-600">
              Entwickelt für CNC-Maschinenwerkstätten. Kombinierbar je nach Beanspruchung und Maschinentyp.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="flex items-start justify-between mb-6">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <Badge variant="outline" className="text-xs">Basisschutz</Badge>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Komplettschutz</h3>
              <p className="text-sm text-gray-500 mb-4 font-medium">Für die gesamte Maschinenscheibe</p>
              
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Maßgeschnittene Schutzfolie. Schützt vor Spänen, Kühlmitteln, Kratzern. Selbstmontage möglich.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Nach Maßen zugeschnitten</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Montageanleitung inklusive</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Für CNC-Fräsen, Bearbeitungszentren</span>
                </li>
              </ul>
              
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">231,50 €</span>
                  <span className="text-sm text-gray-500">netto / Scheibe</span>
                </div>
                <p className="text-xs text-gray-500">Orientierungspreis, abhängig von Größe</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="flex items-start justify-between mb-6">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Star className="h-8 w-8 text-blue-600" />
                </div>
                <Badge variant="outline" className="text-xs">Hotspot-Schutz</Badge>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Verschleißschutz</h3>
              <p className="text-sm text-gray-500 mb-4 font-medium">Industrielle Glasoberfläche</p>
              
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Für stark beanspruchte Hotspot-Bereiche. Trocken aufgelegt, schnell wechselbar. Ideal für Drehmaschinen.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Format: 150 × 300 mm oder Sechseck</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Ohne Kleber – einfach auflegen</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Optimal bei ClearView ultra Beschichtung</span>
                </li>
              </ul>
              
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">136,70 €</span>
                  <span className="text-sm text-gray-500">netto / 5er-Set</span>
                </div>
                <p className="text-xs text-gray-500">Typisch 2-4 Sets pro Maschine</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="flex items-start justify-between mb-6">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Droplets className="h-8 w-8 text-blue-600" />
                </div>
                <Badge variant="outline" className="text-xs">Beschichtung</Badge>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">ClearView ultra</h3>
              <p className="text-sm text-gray-500 mb-4 font-medium">Hydrophobe Nanobeschichtung</p>
              
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Kühlmittel perlt ab, Sicht bleibt klar, Reinigung einfacher. Wirkt am besten auf Glasoberflächen.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Wasserabweisende Wirkung</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Auftrag auf Verschleißschutz oder Scheibe</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <div className="h-1.5 w-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <span className="text-gray-700">Deutlich längere Sichtbarkeit</span>
                </li>
              </ul>
              
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">136,80 €</span>
                  <span className="text-sm text-gray-500">netto / 5er-Set</span>
                </div>
                <p className="text-xs text-gray-500">Anwendung auf Verschleißschutz empfohlen</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Weitere Anwendungen</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Displayschutz für Bedieneinheiten, Strahlkabinenschutz, Kratzschutz für weniger beanspruchte Bereiche. 
                  Individuelle Zuschnitte und Sonderformate auf Anfrage möglich.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="text-xs">Displayschutz</Badge>
                  <Badge variant="secondary" className="text-xs">Strahlkabinen</Badge>
                  <Badge variant="secondary" className="text-xs">Sonderformate</Badge>
                </div>
              </div>
              <Button variant="outline" onClick={() => scrollToSection('contact')}>
                Anfrage stellen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Package Recommendation Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Kombinationsempfehlung</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Paket B: Die Standardlösung für Drehmaschinen
            </h2>
            <p className="text-base text-gray-600">
              Bei Drehmaschinen und Dreh-Fräszentren reicht Komplettschutz allein meist nicht. Hotspot-Bereiche gegenüber Spindel/Futter benötigen zusätzlichen Verschleißschutz.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-6 border-4 border-blue-600 shadow-lg hover:shadow-2xl transition-all">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-5 w-5 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Paket A</h3>
              </div>
              <p className="text-sm text-gray-500 mb-6">Für CNC-Fräsen mit moderater Beanspruchung</p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">1× Komplettschutzfolie</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">2× Verschleißschutz</span>
                </li>
              </ul>
              
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-sm font-medium" onClick={() => scrollToSection('contact')}>
                Paket A anfragen
              </Button>
            </div>

            <div className="relative bg-white rounded-xl p-8 border-4 border-blue-600 shadow-2xl scale-[1.02] lg:scale-105 z-10">
              <div className="absolute -top-4 sm:-top-3 left-1/2 -translate-x-1/2 w-auto whitespace-nowrap">
                <Badge className="bg-blue-600 text-white px-3 sm:px-6 py-1 text-xs sm:text-sm font-bold shadow-lg">
                  Empfohlen für Drehmaschinen
                </Badge>
              </div>
              
              <div className="flex items-center gap-2 mb-4 mt-4 sm:mt-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Paket B</h3>
              </div>
              <p className="text-sm text-blue-700 mb-6 font-medium">Standard für Drehmaschinen / Dreh-Fräszentren</p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-medium">1× Komplettschutzfolie (Gesamtscheibe)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-medium">4× Verschleißschutz (Hotspot-Bereiche)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 font-medium">1× ClearView ultra Set (5 Anwendungen)</span>
                </li>
              </ul>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Warum 4× Verschleißschutz?</strong><br/>
                  Hotspot-Bereiche gegenüber Spindel/Futter erleben extreme Beanspruchung. Verschleißschutz ist dort schnell wechselbar und schützt die darunterliegende Folie.
                </p>
              </div>
              
              <Button className="w-full bg-blue-600 hover:bg-blue-700 font-medium" onClick={() => scrollToSection('contact')}>
                Paket B anfragen
              </Button>
            </div>

            <div className="bg-white rounded-xl p-6 border-4 border-blue-600 shadow-lg hover:shadow-2xl transition-all">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-5 w-5 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Paket C</h3>
              </div>
              <p className="text-sm text-gray-500 mb-6">Für mehrere Maschinen oder größere Projekte</p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Paket B als Basis</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Projektbonus ab 3 Scheiben oder 750 € netto</span>
                </li>
              </ul>
              
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-sm font-medium" onClick={() => scrollToSection('contact')}>
                Paket C anfragen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Application Areas Section */}
      <section id="applications" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Einsatzbereiche</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Für welchen Maschinentyp?
            </h2>
            <p className="text-base text-gray-600">
              Jede Maschine hat spezifische Anforderungen an Scheibenschutz.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="group bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <picture>
                  <source srcSet="/images/cnc-fraesen.webp" type="image/webp" />
                  <img 
                    src="/images/cnc-fraesen.jpg" 
                    alt="CNC-Fräsen"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1200"
                    height="1104"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gauge className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">CNC-Fräsen</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Komplettschutz ausreichend. Bei hoher Spänebelastung Verschleißschutz im Hauptsichtbereich sinnvoll.
                </p>
              </div>
            </div>

            <div className="group bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <picture>
                  <source srcSet="/images/drehmaschinen.webp" type="image/webp" />
                  <img 
                    src="/images/drehmaschinen.jpg" 
                    alt="Drehmaschinen"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="800"
                    height="1200"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Drehmaschinen</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong className="text-gray-900">Paket B empfohlen.</strong> Mehrfacher Verschleißschutz für Hotspot-Bereiche gegenüber Spindel/Futter erforderlich.
                </p>
              </div>
            </div>

            <div className="group bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <picture>
                  <source srcSet="/images/schleifmaschinen.webp" type="image/webp" />
                  <img 
                    src="/images/schleifmaschinen.jpg" 
                    alt="Strahlkabinen"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1200"
                    height="800"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Strahlkabinen</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Spezialfolie für Strahlkabinenscheiben. Schutz gegen Strahlmittel und mechanische Beanspruchung.
                </p>
              </div>
            </div>

            <div className="group bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <picture>
                  <source srcSet="/images/cnc-maschine-sichtfenster.webp" type="image/webp" />
                  <img 
                    src="/images/cnc-maschine-sichtfenster.jpg" 
                    alt="Displays"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1920"
                    height="1280"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Monitor className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Displays & Bedienfelder</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Displayschutz für Touchscreens, Bedieneinheiten und Steuerungsinterfaces an Maschinen.
                </p>
              </div>
            </div>

            <div className="group bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <picture>
                  <source srcSet="/images/sondermaschinen.webp" type="image/webp" />
                  <img 
                    src="/images/sondermaschinen.jpg" 
                    alt="Leicht beanspruchte Bereiche"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="1200"
                    height="800"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Weniger beanspruchte Bereiche</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Kratzschutz für Sichtfenster, Abdeckungen, Schutzverglasungen ohne extreme Beanspruchung.
                </p>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-lg overflow-hidden border-2 border-blue-200 hover:border-blue-400 transition-all">
              <div className="p-8 flex flex-col items-center justify-center h-full text-center min-h-[240px]">
                <Settings className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-3">Spezielle Anwendung?</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Individuelle Zuschnitte, Sonderformate oder unklare Anforderungen? Wir beraten.
                </p>
                <Button variant="outline" size="sm" onClick={() => scrollToSection('contact')}>
                  Anfrage stellen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Ablauf</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              So läuft Ihre Anfrage ab
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-lg p-6 border border-gray-200">
              <div className="absolute -top-4 left-6 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                1
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Maße senden</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Maschinenscheibe in mm (Breite × Höhe), Maschinentyp (z.B. DMG CTX), Einsatzfall kurz beschreiben.
                </p>
              </div>
            </div>

            <div className="relative bg-white rounded-lg p-6 border border-gray-200">
              <div className="absolute -top-4 left-6 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                2
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Empfehlung erhalten</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Konkrete Produktempfehlung (z.B. Paket B), Preisangabe netto, technische Details, Abstimmung bei Bedarf.
                </p>
              </div>
            </div>

            <div className="relative bg-white rounded-lg p-6 border border-gray-200">
              <div className="absolute -top-4 left-6 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                3
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lieferung</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Nach Auftragsbestätigung: Zuschnitt nach Maß, montagefertig verpackt. Typische Bearbeitungszeit ca. 5 Werktage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="bg-gray-100 w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <MapPin className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Hamburg</h3>
                <p className="text-sm text-gray-600">
                  HS Hanse Trade KG<br/>Direkter Kontakt
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gray-100 w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Settings className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Nach Maß</h3>
                <p className="text-sm text-gray-600">
                  Zuschnitt nach Ihren Maßen
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gray-100 w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">CNC-Fokus</h3>
                <p className="text-sm text-gray-600">
                  Für Dreh- und Frästechnik entwickelt
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gray-100 w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Keine Mindestmenge</h3>
                <p className="text-sm text-gray-600">
                  Einzelscheiben möglich
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="flex-shrink-0 w-full md:w-48 h-48 rounded-lg overflow-hidden bg-gray-200">
                  <picture>
                    <source srcSet="/images/praezisionsbearbeitung.webp" type="image/webp" />
                    <img 
                      src="/images/praezisionsbearbeitung.jpg" 
                      alt="Präzisionsbearbeitung"
                      className="w-full h-full object-cover"
                      width="900"
                      height="1200"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Kein Webshop-Automatismus
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">
                    SafeTec+ ist keine anonyme Plattform. Sie haben einen direkten Ansprechpartner, der versteht, 
                    was CNC-Werkstätten brauchen. Produktempfehlung basiert auf Ihrem konkreten Maschinentyp 
                    und Einsatzfall – nicht auf Algorithmen.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong className="text-gray-900">HS Hanse Trade KG</strong> aus Hamburg liefert seit Jahren 
                    industrielle Schutzlösungen. SafeTec+ bündelt das Angebot speziell für CNC-Anwendungen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Häufige Fragen</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Technische Details & Anwendung
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="item-1" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Was ist bei Drehmaschinen sinnvoll?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Paket B ist Standardempfehlung: Komplettschutz plus 4× Verschleißschutz für Hotspot-Bereiche gegenüber Spindel/Futter. Diese Bereiche sind durch Späne und Kühlmittel am stärksten belastet. ClearView ultra verlängert Sichtbarkeit zusätzlich.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Kann ich die Folien selbst montieren?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Ja. Komplettschutz mit Anleitung, Montage mit Sorgfalt gut machbar. Verschleißschutz trocken aufgelegt (kein Kleber) – besonders einfach und schnell wechselbar.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Wann ist Verschleißschutz zusätzlich sinnvoll?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Bei Drehmaschinen immer (Bereich gegenüber Spindel/Futter). Bei CNC-Fräsen mit hoher Spänebelastung im Hauptsichtbereich. Verschleißschutz verlängert Standzeit, ist schnell wechselbar.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Was bringt ClearView ultra konkret?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Hydrophobe Nanobeschichtung: Kühlmittel und Wasser perlen ab, Reinigung leichter, Sichtbarkeit bleibt länger klar. Wirkung am stärksten auf Glasoberflächen (Verschleißschutz). Auftrag einfach, Wirkung direkt sichtbar.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Gibt es individuelle Maße?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Ja, Komplettschutz nach Maß zugeschnitten (Breite × Höhe in mm). Verschleißschutz in Standardformaten: 150 × 300 mm Rechteck oder Sechseck – passt für gängige Hotspot-Bereiche.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Wie läuft Anfrage und Lieferung?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  Anfrage mit Maßen und Maschinentyp → Empfehlung mit Preisangabe → nach Auftragsbestätigung Fertigung → Lieferung typisch ca. 5 Werktage (Standardbearbeitung). Bei Projekten oder speziellen Anforderungen individuelle Abstimmung.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left text-base font-semibold hover:text-blue-600">
                  Für welche Anwendungen ist welche Lösung geeignet?
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gray-600 pt-3 leading-relaxed">
                  <strong>CNC-Fräsen:</strong> Komplettschutz meist ausreichend.<br/>
                  <strong>Drehmaschinen:</strong> Paket B empfohlen (mit Verschleißschutz).<br/>
                  <strong>Strahlkabinen:</strong> Spezialfolie.<br/>
                  <strong>Displays:</strong> Displayschutz.<br/>
                  Bei Unklarheit: Anfrage mit Maschinentyp.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mb-3 bg-blue-100 text-blue-700 hover:bg-blue-100">Anfrage</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Maße senden, Empfehlung erhalten
              </h2>
              <p className="text-base text-gray-600">
                Je genauer Maße und Maschinentyp, desto präziser die Produktempfehlung.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 overflow-hidden">
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="firma" className="block text-sm font-semibold text-gray-900 mb-2">
                        Firma *
                      </label>
                      <Input
                        id="firma"
                        name="firma"
                        value={formData.firma}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="Musterwerkstatt GmbH"
                      />
                    </div>

                    <div>
                      <label htmlFor="vorname" className="block text-sm font-semibold text-gray-900 mb-2">
                        Vorname *
                      </label>
                      <Input
                        id="vorname"
                        name="vorname"
                        value={formData.vorname}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="Max"
                      />
                    </div>

                    <div>
                      <label htmlFor="nachname" className="block text-sm font-semibold text-gray-900 mb-2">
                        Nachname *
                      </label>
                      <Input
                        id="nachname"
                        name="nachname"
                        value={formData.nachname}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="Mustermann"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                        E-Mail *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="m.mustermann@werkstatt.de"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefon" className="block text-sm font-semibold text-gray-900 mb-2">
                        Telefon
                      </label>
                      <Input
                        id="telefon"
                        name="telefon"
                        type="tel"
                        value={formData.telefon}
                        onChange={handleInputChange}
                        className="w-full border-gray-300"
                        placeholder="+49 40 ..."
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="maschinentyp" className="block text-sm font-semibold text-gray-900 mb-2">
                        Maschinentyp *
                      </label>
                      <Input
                        id="maschinentyp"
                        name="maschinentyp"
                        value={formData.maschinentyp}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="z.B. DMG CTX 310, CNC-Fräse"
                      />
                    </div>

                    <div>
                      <label htmlFor="breite" className="block text-sm font-semibold text-gray-900 mb-2">
                        Breite (mm) *
                      </label>
                      <Input
                        id="breite"
                        name="breite"
                        type="number"
                        value={formData.breite}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="800"
                      />
                    </div>

                    <div>
                      <label htmlFor="hoehe" className="block text-sm font-semibold text-gray-900 mb-2">
                        Höhe (mm) *
                      </label>
                      <Input
                        id="hoehe"
                        name="hoehe"
                        type="number"
                        value={formData.hoehe}
                        onChange={handleInputChange}
                        required
                        className="w-full border-gray-300"
                        placeholder="600"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="nachricht" className="block text-sm font-semibold text-gray-900 mb-2">
                      Zusätzliche Angaben
                    </label>
                    <Textarea
                      id="nachricht"
                      name="nachricht"
                      value={formData.nachricht}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full border-gray-300"
                      placeholder="Einsatzfall, Beanspruchung, spezielle Anforderungen..."
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      Ihre Daten werden vertraulich behandelt. Sie erhalten eine konkrete Produktempfehlung mit Preisangabe.
                    </p>
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700 text-base font-medium" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Anfrage senden
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Gated Download Section - Only visible after successful submit */}
                {formSubmittedSuccessfully && (
                  <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg animate-in fade-in duration-500">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span>📥</span> Ihre Unterlagen zum Download
                    </h3>
                    <p className="text-sm text-gray-700 mb-6">
                      Vielen Dank für Ihre Anfrage! Hier sind Ihre kostenlosen Unterlagen:
                    </p>
                    <div className="flex justify-center">
                      <a
                        href={`${API}/download/pdf`}
                        download
                        className="flex items-center justify-center gap-2 bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Produkt & Preisübersicht herunterladen (PDF)
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-100 px-8 py-6 border-t border-gray-200">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">E-Mail</div>
                      <div className="text-sm text-gray-600">anfrage@safetec-plus.de</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Hamburg</div>
                      <div className="text-sm text-gray-600">HS Hanse Trade KG</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Telefon</div>
                      <div className="text-sm text-gray-600">+49 174 2619596</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Reaktionszeit</div>
                      <div className="text-sm text-gray-600">Innerhalb 24h</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Instagram className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Instagram</div>
                      <a 
                        href="https://www.instagram.com/safetec_plus_industrieschutz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        @safetec_plus_industrieschutz
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold mb-3">
                SafeTec<span className="text-blue-400">+</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Maschinenschutz für CNC-Betriebe.<br/>
                Marke der HS Hanse Trade KG.
              </p>
              <p className="text-gray-500 text-xs">
                Hamburg, Deutschland
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-gray-300">Produkte</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('solutions')} className="hover:text-white transition-colors">Komplettschutz</button></li>
                <li><button onClick={() => scrollToSection('solutions')} className="hover:text-white transition-colors">Verschleißschutz</button></li>
                <li><button onClick={() => scrollToSection('solutions')} className="hover:text-white transition-colors">ClearView ultra</button></li>
                <li><button onClick={() => scrollToSection('solutions')} className="hover:text-white transition-colors">Paket B (Drehmaschinen)</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-gray-300">Informationen</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('applications')} className="hover:text-white transition-colors">Einsatzbereiche</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-white transition-colors">Ablauf</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Anfrage stellen</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-gray-300">Kontakt</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="mailto:anfrage@safetec-plus.de" className="hover:text-white transition-colors flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    anfrage@safetec-plus.de
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Hamburg
                </li>
                <li>
                  <a 
                    href="https://www.instagram.com/safetec_plus_industrieschutz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors flex items-center gap-2"
                  >
                    <Instagram className="h-4 w-4" />
                    @safetec_plus_industrieschutz
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
              <div>
                © 2026 SafeTec+ | HS Hanse Trade KG | Hamburg
              </div>
              <div className="flex gap-6">
                <Link to="/impressum" className="hover:text-gray-300 transition-colors">Impressum</Link>
                <Link to="/datenschutz" className="hover:text-gray-300 transition-colors">Datenschutz</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-50"
          aria-label="Scroll to top"
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default Home;
